import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Plus, MessageSquare, Clock, CheckCircle, AlertTriangle, 
  Send, RefreshCw, ChevronRight, X, HelpCircle
} from 'lucide-react';
import api from '../../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SupportTickets = () => {
  const { t } = useTranslation();
  const user = JSON.parse(localStorage.getItem('movemate_user') || '{}');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketDialog, setShowTicketDialog] = useState(false);
  const [replies, setReplies] = useState([]);
  const [replyMessage, setReplyMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    category: 'other',
    priority: 'medium'
  });

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus]);

  const fetchTickets = async () => {
    try {
      const statusParam = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
      const response = await api.get(`/support/tickets${statusParam}`);
      setTickets(response.data.tickets || []);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await api.post('/support/tickets', formData);
      toast.success(`Ticket created! Reference: ${response.data.ticket_number}`);
      setShowCreateDialog(false);
      setFormData({ subject: '', description: '', category: 'other', priority: 'medium' });
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const openTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setShowTicketDialog(true);
    
    try {
      const response = await api.get(`/support/tickets/${ticket.id}`);
      setSelectedTicket(response.data.ticket);
      setReplies(response.data.replies || []);
    } catch (error) {
      toast.error('Failed to load ticket details');
    }
  };

  const handleReply = async () => {
    if (!replyMessage.trim()) return;
    setSubmitting(true);
    
    try {
      await api.post(`/support/tickets/${selectedTicket.id}/reply`, {
        message: replyMessage
      });
      toast.success('Reply sent');
      setReplyMessage('');
      
      // Refresh ticket and replies
      const response = await api.get(`/support/tickets/${selectedTicket.id}`);
      setSelectedTicket(response.data.ticket);
      setReplies(response.data.replies || []);
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseTicket = async () => {
    try {
      await api.post(`/support/tickets/${selectedTicket.id}/close`);
      toast.success('Ticket closed');
      setShowTicketDialog(false);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to close ticket');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      open: { color: 'bg-blue-100 text-blue-700', label: 'Open' },
      in_progress: { color: 'bg-yellow-100 text-yellow-700', label: 'In Progress' },
      waiting_response: { color: 'bg-purple-100 text-purple-700', label: 'Awaiting Reply' },
      resolved: { color: 'bg-green-100 text-green-700', label: 'Resolved' },
      closed: { color: 'bg-gray-100 text-gray-700', label: 'Closed' }
    };
    const config = statusConfig[status] || statusConfig.open;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      low: { color: 'bg-gray-100 text-gray-600', label: 'Low' },
      medium: { color: 'bg-blue-100 text-blue-600', label: 'Medium' },
      high: { color: 'bg-orange-100 text-orange-600', label: 'High' },
      urgent: { color: 'bg-red-100 text-red-600', label: 'Urgent' }
    };
    const config = priorityConfig[priority] || priorityConfig.medium;
    return <Badge variant="outline" className={config.color}>{config.label}</Badge>;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      technical: 'Technical Issue',
      billing: 'Billing',
      feature_request: 'Feature Request',
      bug_report: 'Bug Report',
      account: 'Account',
      other: 'Other'
    };
    return labels[category] || category;
  };

  return (
    <div className="space-y-6" data-testid="support-tickets">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold mb-2">{t('support.title', 'Support')}</h1>
          <p className="text-gray-600">{t('support.subtitle', 'Get help from the MoveMate team')}</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-ticket-btn">
              <Plus size={20} className="mr-2" />
              {t('support.new_ticket', 'New Ticket')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('support.create_ticket', 'Create Support Ticket')}</DialogTitle>
              <DialogDescription>
                {t('support.create_description', "Describe your issue and we'll get back to you as soon as possible.")}
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleCreateTicket} className="space-y-4">
              <div>
                <Label>{t('support.subject', 'Subject')} *</Label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Brief description of your issue"
                  required
                  data-testid="ticket-subject"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger data-testid="ticket-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="technical">Technical Issue</SelectItem>
                      <SelectItem value="billing">Billing</SelectItem>
                      <SelectItem value="feature_request">Feature Request</SelectItem>
                      <SelectItem value="bug_report">Bug Report</SelectItem>
                      <SelectItem value="account">Account</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select 
                    value={formData.priority} 
                    onValueChange={(value) => setFormData({ ...formData, priority: value })}
                  >
                    <SelectTrigger data-testid="ticket-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Please describe your issue in detail. Include any error messages or steps to reproduce the problem."
                  rows={5}
                  required
                  data-testid="ticket-description"
                />
              </div>
              
              <Button type="submit" className="w-full" disabled={submitting} data-testid="submit-ticket-btn">
                {submitting ? 'Creating...' : 'Submit Ticket'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {['all', 'open', 'in_progress', 'waiting_response', 'resolved', 'closed'].map((status) => (
          <Button
            key={status}
            variant={filterStatus === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus(status)}
          >
            {status === 'all' ? 'All' : status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </Button>
        ))}
      </div>

      {/* Tickets List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <HelpCircle size={20} />
              Your Tickets ({tickets.length})
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={fetchTickets}>
              <RefreshCw size={16} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="mx-auto text-gray-400 mb-4" size={48} />
              <p className="text-gray-600 mb-2">No tickets found</p>
              <p className="text-sm text-gray-500">Create a new ticket to get help from our team</p>
            </div>
          ) : (
            <div className="space-y-3">
              {tickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className="p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => openTicket(ticket)}
                  data-testid={`ticket-${ticket.id}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-gray-500 font-mono">
                          #{ticket.id.substring(0, 8).toUpperCase()}
                        </span>
                        {getStatusBadge(ticket.status)}
                        {getPriorityBadge(ticket.priority)}
                      </div>
                      <h3 className="font-semibold text-lg">{ticket.subject}</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {getCategoryLabel(ticket.category)} • Created {new Date(ticket.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <ChevronRight className="text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ticket Detail Dialog */}
      <Dialog open={showTicketDialog} onOpenChange={setShowTicketDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          {selectedTicket && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <DialogTitle className="pr-8">{selectedTicket.subject}</DialogTitle>
                    <DialogDescription>
                      <span className="font-mono">#{selectedTicket.id.substring(0, 8).toUpperCase()}</span>
                      {' • '}{getCategoryLabel(selectedTicket.category)}
                    </DialogDescription>
                  </div>
                  <div className="flex gap-2">
                    {getStatusBadge(selectedTicket.status)}
                    {getPriorityBadge(selectedTicket.priority)}
                  </div>
                </div>
              </DialogHeader>
              
              <div className="flex-1 overflow-y-auto space-y-4 py-4">
                {/* Original Message */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-semibold">{selectedTicket.created_by_name}</span>
                    <span className="text-xs text-gray-500">
                      {new Date(selectedTicket.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="whitespace-pre-wrap text-gray-700">{selectedTicket.description}</p>
                </div>
                
                {/* Replies */}
                {replies.map((reply) => (
                  <div
                    key={reply.id}
                    className={`p-4 rounded-lg ${
                      reply.user_role === 'admin' 
                        ? 'bg-green-50 border-l-4 border-green-500' 
                        : 'bg-blue-50'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold">
                        {reply.user_role === 'admin' ? 'MoveMate Support' : reply.user_name}
                      </span>
                      {reply.user_role === 'admin' && (
                        <Badge variant="outline" className="text-xs">Support</Badge>
                      )}
                      <span className="text-xs text-gray-500">
                        {new Date(reply.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="whitespace-pre-wrap text-gray-700">{reply.message}</p>
                  </div>
                ))}
              </div>
              
              {/* Reply Form */}
              {selectedTicket.status !== 'closed' && (
                <div className="border-t pt-4 space-y-3">
                  <Textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder="Type your reply..."
                    rows={3}
                    data-testid="ticket-reply-input"
                  />
                  <div className="flex justify-between">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleCloseTicket}
                    >
                      <CheckCircle size={16} className="mr-2" />
                      Close Ticket
                    </Button>
                    <Button 
                      onClick={handleReply} 
                      disabled={!replyMessage.trim() || submitting}
                      data-testid="send-reply-btn"
                    >
                      <Send size={16} className="mr-2" />
                      {submitting ? 'Sending...' : 'Send Reply'}
                    </Button>
                  </div>
                </div>
              )}
              
              {selectedTicket.status === 'closed' && (
                <div className="border-t pt-4 text-center text-gray-500">
                  <CheckCircle className="inline-block mr-2 text-green-500" size={20} />
                  This ticket has been closed
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupportTickets;
